import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Crown,
  CreditCard,
  Sparkles,
  Check,
  ExternalLink,
  Ticket,
  ArrowLeft,
  ChevronRight,
} from 'lucide-react';
import { useSubscription, PRO_FEATURES, FEATURE_INFO } from '../hooks/useSubscription';
import toast from 'react-hot-toast';

export default function BillingPage() {
  const {
    status,
    plan,
    hasProAccess,
    hasStripeSubscription,
    isTrialing,
    hasLifetimeAccess,
    trialDaysRemaining,
    canStartTrial,
    displayStatus,
    cancelAtPeriodEnd,
    startFreeTrial,
    openCheckout,
    openBillingPortal,
    redeemPromoCode,
    isLoading,
    error,
  } = useSubscription();

  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  const handleStartTrial = async () => {
    try {
      await startFreeTrial();
      toast.success('Your 14-day free trial has started!');
    } catch (err) {
      toast.error(err.message || 'Failed to start trial');
    }
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return;

    try {
      const result = await redeemPromoCode(promoCode);
      toast.success(result.message || 'Promo code applied!');
      setShowPromoInput(false);
      setPromoCode('');
    } catch (err) {
      toast.error(err.message || 'Invalid promo code');
    }
  };

  const featureList = Object.values(PRO_FEATURES).map(key => FEATURE_INFO[key]);

  return (
    <div className="pb-20 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          to="/settings"
          className="p-2 rounded-xl bg-surface-card border border-border hover:border-accent/50 transition-colors"
        >
          <ArrowLeft size={16} className="text-text-secondary" />
        </Link>
        <div>
          <h1 className="text-lg font-semibold text-text-primary">Billing</h1>
          <p className="text-xs text-text-tertiary">Manage your subscription</p>
        </div>
      </div>

      {/* Current Plan */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <Crown size={14} />
          Current Plan
        </h2>
        <div className="bg-surface-card rounded-xl border border-border p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                hasProAccess
                  ? 'bg-gradient-to-br from-accent/20 to-orange-500/20'
                  : 'bg-surface-elevated'
              }`}>
                <Crown size={20} className={hasProAccess ? 'text-accent' : 'text-text-tertiary'} />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">{displayStatus}</p>
                {plan && (
                  <p className="text-xs text-text-tertiary">
                    {plan === 'annual' ? '$96/year' : '$10/month'}
                  </p>
                )}
                {isTrialing && trialDaysRemaining !== null && (
                  <p className="text-xs text-warning">{trialDaysRemaining} days left</p>
                )}
                {cancelAtPeriodEnd && (
                  <p className="text-xs text-error">Cancels at period end</p>
                )}
              </div>
            </div>
            {hasProAccess && (
              <Check size={18} className="text-success" />
            )}
          </div>
        </div>
      </section>

      {/* Upgrade / Manage Section */}
      {!hasLifetimeAccess && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
            <CreditCard size={14} />
            {hasStripeSubscription ? 'Manage' : 'Upgrade'}
          </h2>
          <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
            {hasStripeSubscription ? (
              <button
                onClick={openBillingPortal}
                disabled={isLoading}
                className="w-full flex items-center justify-between p-4 hover:bg-surface-hover transition-colors disabled:opacity-50 bg-transparent border-none cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <CreditCard size={18} className="text-accent" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-text-primary">Manage Subscription</p>
                    <p className="text-xs text-text-tertiary">Update payment, cancel, or change plan</p>
                  </div>
                </div>
                <ExternalLink size={14} className="text-text-tertiary" />
              </button>
            ) : (
              <>
                {/* Free Trial Button */}
                {canStartTrial && (
                  <button
                    onClick={handleStartTrial}
                    disabled={isLoading}
                    className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-accent to-orange-500 text-white hover:opacity-90 transition-opacity disabled:opacity-50 border-none cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <Sparkles size={18} />
                      <div className="text-left">
                        <p className="text-sm font-medium">Start Free Trial</p>
                        <p className="text-xs opacity-80">14 days, no card required</p>
                      </div>
                    </div>
                    <ChevronRight size={16} />
                  </button>
                )}

                {/* Trial active - show subscribe prompt */}
                {isTrialing && (
                  <div className="p-4 bg-accent/5 border-b border-border">
                    <p className="text-xs text-text-secondary">
                      Your trial is active. Subscribe now to continue after it ends.
                    </p>
                  </div>
                )}

                {/* Annual Plan */}
                <button
                  onClick={() => openCheckout('annual')}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-between p-4 hover:bg-surface-hover transition-colors disabled:opacity-50 bg-transparent cursor-pointer ${canStartTrial || isTrialing ? 'border-t border-border' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-text-primary">Annual</p>
                        <span className="px-1.5 py-0.5 text-[10px] font-bold text-white bg-success rounded">
                          SAVE 20%
                        </span>
                      </div>
                      <p className="text-xs text-text-tertiary">$8/mo billed yearly</p>
                    </div>
                  </div>
                  <ExternalLink size={14} className="text-text-tertiary" />
                </button>

                {/* Monthly Plan */}
                <button
                  onClick={() => openCheckout('monthly')}
                  disabled={isLoading}
                  className="w-full flex items-center justify-between p-4 border-t border-border hover:bg-surface-hover transition-colors disabled:opacity-50 bg-transparent cursor-pointer"
                >
                  <div className="text-left">
                    <p className="text-sm font-medium text-text-primary">Monthly</p>
                    <p className="text-xs text-text-tertiary">$10/month</p>
                  </div>
                  <ExternalLink size={14} className="text-text-tertiary" />
                </button>
              </>
            )}
          </div>
        </section>
      )}

      {/* Promo Code */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <Ticket size={14} />
          Promo Code
        </h2>
        <div className="bg-surface-card rounded-xl border border-border p-4">
          {!showPromoInput ? (
            <button
              onClick={() => setShowPromoInput(true)}
              className="flex items-center gap-2 text-sm text-text-primary hover:text-accent transition-colors bg-transparent border-none cursor-pointer p-0"
            >
              <Ticket size={16} />
              <span>Have a promo code?</span>
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="flex-1 py-2 px-3 bg-surface-elevated border border-border rounded-lg text-sm text-text-primary placeholder:text-text-muted outline-none focus:border-accent"
                />
                <button
                  onClick={handleRedeemPromo}
                  disabled={isLoading || !promoCode.trim()}
                  className="px-4 py-2 bg-accent text-white font-medium rounded-lg text-xs border-none cursor-pointer hover:bg-accent/90 transition-colors disabled:opacity-50"
                >
                  Redeem
                </button>
              </div>
              <button
                onClick={() => {
                  setShowPromoInput(false);
                  setPromoCode('');
                }}
                className="text-xs text-text-tertiary hover:text-text-secondary bg-transparent border-none cursor-pointer p-0"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Pro Features */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <Sparkles size={14} />
          Pro Features
        </h2>
        <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
          {featureList.map((feature, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 p-3 ${index > 0 ? 'border-t border-border' : ''}`}
            >
              <div className={`${hasProAccess ? 'text-success' : 'text-text-tertiary'}`}>
                <Check size={14} />
              </div>
              <div className="flex-1">
                <p className="text-sm text-text-primary">{feature.name}</p>
                <p className="text-xs text-text-tertiary">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-xs text-red-400">
          {error}
        </div>
      )}
    </div>
  );
}
